import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import './design/tokens.css'
import './design/base.css'
import './design/motion.css'
import { App } from './App.js'
import { BlobExporter } from './adapters/blob-exporter.js'
import { CryptoIds } from './adapters/crypto-ids.js'
import { openDatabase } from './adapters/indexeddb/dexie-storage.js'
import { SystemClock } from './adapters/system-clock.js'
import { WebNotifier } from './adapters/web-notifier.js'
import { StartupError } from './shell/StartupError.js'

const container = document.getElementById('root')
if (!container) {
  throw new Error('Root element #root is missing from index.html')
}

const root = createRoot(container)

/**
 * The one place concrete adapters are chosen.
 *
 * No screen names a storage implementation, so this is the whole of what
 * changed when T-203 replaced `InMemoryStorage` with IndexedDB — which is the
 * injection boundary from T-201 doing the job it exists for, and the same shape
 * the native app will use to supply SQLite.
 *
 * Opening is awaited before the first render rather than resolved inside a
 * component. Every screen would otherwise need a loading branch for a state
 * that lasts a few milliseconds and, on failure, can never resolve.
 */
try {
  const storage = await openDatabase()
  root.render(
    <StrictMode>
      <App
        storage={storage}
        clock={new SystemClock()}
        ids={new CryptoIds()}
        exporter={new BlobExporter()}
        notifier={new WebNotifier()}
      />
    </StrictMode>,
  )
} catch (error) {
  // A failure here is not a crash to swallow. Refusing to open a database
  // written by a newer build is a deliberate, correct outcome (ADR-0003), and
  // it has to say so — a blank page reads as "my data is gone", which is
  // exactly the wrong conclusion.
  console.error('Could not open the database', error)
  root.render(
    <StrictMode>
      <StartupError error={error} />
    </StrictMode>,
  )
}
